import type { BetterFeaturePluginSchema } from "@di-devs/better-feature/types";
import { z } from "zod";
import { MEMBERSHIP_STATUSES } from "../constants/membership.constants";

/**
 * Better Feature model schema definition
 * Defines the structure and validation rules for member data
 */
export const memberBetterFeatureSchema = {
	member: {
		fields: {
			memberId: {
				type: "number",
				required: true,
				sortable: true,
				unique: true,
				returned: true,
			},
			displayName: {
				type: "string",
				required: false,
				sortable: true,
				returned: true,
			},
			picUrl: {
				type: "string",
				required: false,
				returned: true,
			},
			unreadCount: {
				type: "number",
				required: false,
				returned: true,
			},
			createdAt: {
				type: "date",
				required: false,
				sortable: true,
				returned: true,
			},
			memberSince: {
				type: "date",
				required: false,
				sortable: true,
				returned: true,
			},
			activeUntil: {
				type: "date",
				required: false,
				sortable: true,
				returned: true,
			},
			membershipStatus: {
				type: "string",
				required: false,
				sortable: true,
				returned: true,
			},
			membershipStatusUpdatedAt: {
				type: "date",
				required: false,
				returned: true,
			},

			preExpiryReminderSentAt: {
				type: "date",
				required: false,
				returned: true,
			},
			expiryReminderSentAt: {
				type: "date",
				required: false,
				returned: true,
			},
			postExpiryReminderSentAt: {
				type: "date",
				required: false,
				returned: true,
			},
			deactivationReminderSentAt: {
				type: "date",
				required: false,
				returned: true,
			},
			membershipDeactivatedAt: {
				type: "date",
				required: false,
				returned: true,
			},
			firstName: {
				type: "string",
				required: false,
				returned: true,
			},
			lastName: {
				type: "string",
				required: false,
				returned: true,
			},
			firstNameKana: {
				type: "string",
				required: false,
				returned: true,
			},
			lastNameKana: {
				type: "string",
				required: false,
				returned: true,
			},
			telephone: {
				type: "string",
				required: false,
				returned: true,
			},
			address: {
				type: "string",
				required: false,
				returned: true,
			},
			isRegistered: {
				type: "boolean",
				required: false,
				returned: true,
			},
			isFriends: {
				type: "boolean",
				required: false,
				returned: true,
			},
			lastVisitDate: {
				type: "date",
				required: false,
				sortable: true,
				returned: true,
			},
			visitCount: {
				type: "number",
				required: false,
				returned: true,
			},
			pointBalance: {
				type: "number",
				required: false,
				returned: true,
			},
			messages: {
				type: "string",
				required: false,
				returned: true,
			},
			remarks: {
				type: "string",
				required: false,
				returned: true,
			},
			// Legacy aliases for backward compatibility
			lastVisit: {
				type: "date",
				required: false,
				sortable: true,
				returned: true,
			},
			kakeruPoint: {
				type: "number",
				required: false,
				returned: true,
			},
			countVisit: {
				type: "number",
				required: false,
				returned: true,
			},
		},
		modelName: "member",
	},
} satisfies BetterFeaturePluginSchema;

/**
 * Core Member Zod schema for validation
 * Based on the original member model structure
 */
export const memberCoreSchema = z.object({
	memberId: z.coerce.number().int().optional(),
	lineId: z.coerce.string().optional(),
	displayName: z.coerce.string().optional(),
	picUrl: z.coerce.string().optional(),
	unreadCount: z.coerce.number().int().default(0),
	memberSince: z.coerce.date().optional(),
	memberCode: z.coerce.string().optional(),
	curRM: z.coerce.string().optional(),
	isRegistered: z.coerce.boolean().optional(),
	isFriends: z.coerce.boolean().optional(),
	isCampaign: z.coerce.boolean().optional(),
	activeUntil: z.coerce.date().optional(),
	membershipStatus: z
		.enum(MEMBERSHIP_STATUSES)
		.default("provisional")
		.optional(),
	membershipStatusUpdatedAt: z.coerce.date().nullable().optional(),

	preExpiryReminderSentAt: z.coerce.date().nullable().optional(),
	expiryReminderSentAt: z.coerce.date().nullable().optional(),
	postExpiryReminderSentAt: z.coerce.date().nullable().optional(),
	deactivationReminderSentAt: z.coerce.date().nullable().optional(),
	membershipDeactivatedAt: z.coerce.date().nullable().optional(),
	kakeruPoint: z.coerce.number().int().default(0),
	remarks: z.coerce.string().optional(),
	createdAt: z.coerce.date().optional(),
	updatedAt: z.coerce.date().optional(),
});

/**
 * Pagination schema for member browse operations
 */
export const paginationSchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	perPage: z.coerce.number().int().min(1).max(100).default(20),
	sort: z.enum(["asc", "desc"]).default("desc"),
	sortKey: z.string().default("memberId"),
});

/**
 * Member browse query parameters schema
 */
export const memberBrowseQuerySchema = z
	.object({
		// Pagination
		pp: z.coerce.number().int().min(1).max(100).default(20).optional(),
		p: z.coerce.number().int().min(1).default(1).optional(),
		sort: z.enum(["asc", "desc"]).default("desc").optional(),
		sortKey: z.string().default("memberId").optional(),

		// Basic filters
		lineName: z.coerce.string().optional(),
		messages: z.enum(["read", "unread"]).optional(),
		isFriends: z.coerce
			.string()
			.transform((val) => val === "true" || val === "1")
			.refine((val) => typeof val === "boolean", {
				message:
					"Query param must be 'true' or '1', otherwise it will default to false",
			})
			.optional(),
		isRegistered: z.coerce
			.string()
			.transform((val) => val === "true" || val === "1")
			.refine((val) => typeof val === "boolean", {
				message:
					"Query param must be 'true' or '1', otherwise it will default to false",
			})
			.optional(),
		membershipStatus: z.enum(MEMBERSHIP_STATUSES).optional(),

		// Date filters
		createdAtMin: z.coerce.string().optional(),
		createdAtMax: z.coerce.string().optional(),
		memberSinceMin: z.coerce.string().optional(),
		memberSinceMax: z.coerce.string().optional(),
		lastVisitMin: z.coerce.string().optional(),
		lastVisitMax: z.coerce.string().optional(),
	})
	.passthrough(); // Allow member attribute filters

/**
 * Member update request schema
 */
export const memberUpdateSchema = z
	.object({
		activeUntil: z.coerce.string().optional(),
		remarks: z.coerce.string().optional(),
		memberSince: z.coerce.string().optional(),
		kakeruPoint: z.coerce.number().optional(),
		kakeruPointIsAdd: z.coerce.boolean().optional(),
		visitDate: z.coerce.string().optional(),
		memberVisitId: z.coerce.number().optional(),
	})
	.passthrough(); // Allow additional member attribute fields

/**
 * Member CSV generation request schema
 */
export const memberCSVGenerationSchema = z.object({
	password: z.coerce.string(),
	isCampaign: z.coerce.boolean().optional().default(false),
});

/**
 * Member response schema
 */
export const memberResponseSchema = z.object({
	...memberCoreSchema.shape,
	countVisit: z.coerce.number().int().optional(),
	lastVisit: z.coerce.date().optional(),
	kakeruPoints: z
		.array(
			z.object({
				point: z.coerce.number(),
				source: z.coerce.string(),
				processedAt: z.coerce.date(),
			}),
		)
		.optional(),
	visits: z
		.array(
			z.object({
				visitDate: z.coerce.date(),
				memberVisitId: z.coerce.number(),
			}),
		)
		.optional(),
});

/**
 * Member browse response schema
 */
export const memberBrowseResponseSchema = z.object({
	rows: z.array(memberResponseSchema),
	count: z.coerce.number().int(),
	p: z.coerce.number().int(),
	pp: z.coerce.number().int(),
	sort: z.enum(["asc", "desc"]),
	sortKey: z.coerce.string(),
});

/**
 * Standard success response schema
 */
export const successResponseSchema = z.object({
	success: z.literal(true),
	message: z.coerce.string().optional(),
});

/**
 * Error response schema
 */
export const errorResponseSchema = z.object({
	success: z.literal(false),
	message: z.coerce.string(),
	code: z.coerce.string().optional(),
});
